import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";

/**
 * Karmpatra — read-only archive of KARM's editorial letters.
 * Numbered issues, newest on top, deep-editorial dark theme (matches the
 * Vital Krriya / New Yorker letter aesthetic supplied by the user).
 *
 * SCOPED CSS: this page carries its own palette so it stays consistent
 * regardless of KARM's light/dark toggle — the letter reads the same at
 * 4pm and at midnight.
 */
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return ""; }
};

export default function KarmpatraListPage() {
  useSEO({
    title: "Karmpatra — Letters from KARM Editorial | Global GCC Careers",
    description: "Numbered letters from KARM Editorial — dispatches on Global Capability Centres worldwide, career craft, and the quiet economics of work.",
    path: "/karmpatra",
  });

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/karmpatra", { params: { limit: 50 } })
      .then((r) => {
        if (!alive) return;
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  return (
    <div className="kpatra-scope min-h-screen" data-testid="karmpatra-list">
      <style>{KPATRA_CSS}</style>

      <header className="kp-masthead">
        <p className="kp-brand-label">कर्म · Letters from KARM Editorial</p>
        <h1 className="kp-brand-name">Karmpatra</h1>
        <p className="kp-tagline">
          Numbered dispatches on Global Capability Centres worldwide, career craft,
          and the quiet economics of work. Read newest first.
        </p>
        <div className="kp-divider"><span>✦</span></div>
      </header>

      <main className="kp-container">
        {loading ? (
          <p className="kp-empty">Loading the archive…</p>
        ) : items.length === 0 ? (
          <p className="kp-empty">The presses are warming. First letter shortly.</p>
        ) : (
          <ol className="kp-issue-list" data-testid="karmpatra-issue-list">
            {items.map((it) => (
              <li key={it.issue_id} className="kp-issue" data-testid={`karmpatra-issue-${it.issue_number}`}>
                <Link to={`/karmpatra/${it.slug}`} className="kp-issue-link">
                  <div className="kp-issue-num">
                    Letter <span className="kp-issue-num-value">No.&nbsp;{String(it.issue_number).padStart(2, "0")}</span>
                  </div>
                  <h2 className="kp-issue-title">{it.title}</h2>
                  {it.dek && <p className="kp-issue-dek">{it.dek}</p>}
                  <div className="kp-issue-meta">
                    <span>{it.author || "KARM Editorial"}</span>
                    {it.published_at && <><span className="kp-dot">·</span><span>{fmtDate(it.published_at)}</span></>}
                    {it.tags?.length > 0 && <><span className="kp-dot">·</span><span>{it.tags.slice(0, 3).join(" · ")}</span></>}
                  </div>
                </Link>
              </li>
            ))}
          </ol>
        )}
      </main>

      <footer className="kp-footer">
        <div className="kp-divider"><span>✦</span></div>
        <p className="kp-brand-label">Karmpatra · A KARM publication</p>
        <p className="kp-footer-line">
          {total.toLocaleString()} letter{total === 1 ? "" : "s"} archived.{" "}
          <Link to="/" className="kp-back">Return to KARM →</Link>
        </p>
      </footer>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────
   Scoped stylesheet — used by both KarmpatraListPage and
   KarmpatraIssuePage. Kept inline so the editorial palette
   travels with the two routes without polluting global CSS.
   ──────────────────────────────────────────────────────────── */
export const KPATRA_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Jost:wght@300;400;500&display=swap');

.kpatra-scope {
  background: #0f0e0b;
  color: #ddd6c8;
  font-family: 'Jost', system-ui, sans-serif;
  font-weight: 300;
  min-height: 100vh;
  padding: 4rem 1.5rem 6rem;
  -webkit-user-select: text;
  user-select: text;
}

.kp-masthead {
  max-width: 720px;
  margin: 0 auto 4rem;
  text-align: center;
}
.kp-brand-label {
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #8a7659;
  margin: 0 0 1.5rem;
}
.kp-brand-name {
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-weight: 300;
  font-size: 56px;
  color: #c8b48a;
  margin: 0 0 1rem;
  letter-spacing: -0.01em;
}
.kp-tagline {
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-size: 18px;
  line-height: 1.75;
  color: #7a6e5e;
  margin: 0 auto;
  max-width: 520px;
}
.kp-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 3rem auto;
  max-width: 480px;
  color: #8a7659;
}
.kp-divider::before,
.kp-divider::after {
  content: "";
  flex: 1;
  height: 0.5px;
  background: rgba(138, 118, 89, 0.28);
}
.kp-divider span {
  padding: 0 1.5rem;
  font-family: 'Cormorant Garamond', serif;
  font-size: 14px;
  color: #c8b48a;
}

.kp-container {
  max-width: 720px;
  margin: 0 auto;
}
.kp-empty {
  text-align: center;
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  color: #7a6e5e;
  padding: 4rem 0;
}
.kp-issue-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.kp-issue {
  border-bottom: 0.5px solid rgba(138, 118, 89, 0.22);
  padding: 2.5rem 0;
}
.kp-issue:first-child { padding-top: 0; }
.kp-issue-link {
  display: block;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.25s ease;
}
.kp-issue-link:hover { opacity: 0.85; }
.kp-issue-link:hover .kp-issue-title { color: #c8b48a; }

.kp-issue-num {
  font-size: 11px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #8a7659;
  margin-bottom: 0.75rem;
}
.kp-issue-num-value {
  color: #c8b48a;
  font-weight: 400;
}
.kp-issue-title {
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-weight: 300;
  font-size: 32px;
  line-height: 1.2;
  color: #ddd6c8;
  margin: 0 0 0.6rem;
  transition: color 0.25s ease;
}
.kp-issue-dek {
  font-size: 16px;
  line-height: 1.7;
  color: #a89985;
  margin: 0 0 0.8rem;
  max-width: 600px;
}
.kp-issue-meta {
  font-size: 12px;
  color: #7a6e5e;
  letter-spacing: 0.04em;
}
.kp-dot { margin: 0 0.6rem; opacity: 0.6; }

.kp-footer {
  max-width: 720px;
  margin: 6rem auto 0;
  text-align: center;
}
.kp-footer-line {
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  color: #7a6e5e;
  font-size: 14px;
  margin: 0.8rem 0 0;
}
.kp-back {
  color: #c8b48a;
  text-decoration: none;
  border-bottom: 0.5px solid rgba(200, 180, 138, 0.4);
  transition: border-color 0.25s ease;
}
.kp-back:hover { border-color: #c8b48a; }

/* ── Issue detail body ── */
.kp-issue-body {
  max-width: 640px;
  margin: 0 auto;
  font-size: 17px;
  line-height: 1.9;
  color: #ddd6c8;
}
.kp-issue-body p { margin: 0 0 1.4rem; }
.kp-issue-body h2, .kp-issue-body h3 {
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-weight: 400;
  color: #c8b48a;
  margin-top: 2.5rem;
  margin-bottom: 1rem;
  line-height: 1.25;
}
.kp-issue-body h2 { font-size: 28px; }
.kp-issue-body h3 { font-size: 22px; }
.kp-issue-body a {
  color: #c8b48a;
  border-bottom: 0.5px solid rgba(200, 180, 138, 0.4);
  text-decoration: none;
}
.kp-issue-body a:hover { border-color: #c8b48a; }
.kp-issue-body em {
  font-family: 'Cormorant Garamond', serif;
  color: #c8b48a;
}
.kp-issue-body blockquote {
  border-left: 1px solid rgba(200, 180, 138, 0.35);
  padding: 0.5rem 0 0.5rem 1.5rem;
  margin: 2rem 0;
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-size: 20px;
  line-height: 1.7;
  color: #c8b48a;
}
.kp-issue-body img { max-width: 100%; height: auto; margin: 2rem auto; display: block; }
.kp-issue-body ul, .kp-issue-body ol { padding-left: 1.5rem; margin: 0 0 1.4rem; }
.kp-issue-body li { margin-bottom: 0.6rem; }
.kp-issue-body code {
  font-family: 'Menlo', monospace;
  font-size: 14px;
  background: rgba(200, 180, 138, 0.08);
  padding: 0.1em 0.4em;
  border-radius: 2px;
}
.kp-issue-body hr {
  border: none;
  border-top: 0.5px solid rgba(138, 118, 89, 0.28);
  margin: 3rem 0;
}

.kp-issue-hero-title {
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-weight: 300;
  font-size: 44px;
  line-height: 1.15;
  color: #ddd6c8;
  margin: 0 0 1rem;
  text-align: center;
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
}
.kp-issue-hero-dek {
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-size: 20px;
  color: #a89985;
  text-align: center;
  max-width: 560px;
  margin: 0 auto 1.5rem;
  line-height: 1.6;
}
.kp-issue-hero-meta {
  text-align: center;
  font-size: 12px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #8a7659;
  margin-bottom: 2.5rem;
}
.kp-issue-hero-num {
  font-size: 11px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #8a7659;
  text-align: center;
  margin-bottom: 1.2rem;
}
.kp-issue-hero-num strong { color: #c8b48a; font-weight: 500; }

.kp-nav {
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  max-width: 720px;
  margin: 5rem auto 0;
  padding-top: 3rem;
  border-top: 0.5px solid rgba(138, 118, 89, 0.22);
}
.kp-nav a {
  color: inherit;
  text-decoration: none;
  flex: 1;
  max-width: 300px;
  transition: opacity 0.25s ease;
}
.kp-nav a:hover { opacity: 0.85; }
.kp-nav-label {
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #8a7659;
  margin-bottom: 0.4rem;
}
.kp-nav-title {
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-size: 17px;
  color: #ddd6c8;
  line-height: 1.35;
}
.kp-nav-next { text-align: right; }

@media (max-width: 640px) {
  .kpatra-scope { padding: 2.5rem 1.25rem 4rem; }
  .kp-brand-name { font-size: 42px; }
  .kp-issue-title { font-size: 24px; }
  .kp-issue-hero-title { font-size: 32px; }
  .kp-issue-body { font-size: 16px; line-height: 1.8; }
  .kp-nav { flex-direction: column; gap: 1.5rem; }
  .kp-nav-next { text-align: left; }
}
`;
